<script setup async lang="ts">
import { LOCALE_CATEGORIES } from '@/assets/locales/categories';
import { useSessionStore } from '@/domains/-session/stores/useSessionStore';
import { loadLocales } from '@/i18n';

await loadLocales(LOCALE_CATEGORIES.LEGAL)

const session = useSessionStore()
</script>

<template>
    <div class="legal-terms">
        <div class="legal-terms-inner">
            <template v-if="session.lang === 'fr'">
                <h1>Conditions Générales d’Utilisation (CGU) </h1>
                <p>Dernière mise à jour : 7 juillet 2026</p>
                <br>
                <ol>
                    <li>
                        <h2>Préambule</h2>
                        <p>Les présentes Conditions Générales d’Utilisation (CGU) encadrent les conditions d’accès, les modalités d’utilisation des services et les droits et obligations respectifs des Utilisateurs lors de l’utilisation des services disponibles via la Plateforme ai.omind.me (ci-après « la Plateforme ») développée par Open Mind Innovation. </p>
                    </li>

                    <li>
                        <h2>Directeur de la publication et hébergeur</h2>
                        <p>
                            <span>Open Mind Innovation SAS</span><br>
                            <span>Siège social : 7 Place du 11 Novembre 1918 - 93000 Bobigny (FRANCE)</span><br>
                            <span>Téléphone : +33 (0)6 59 35 99 63</span><br>
                            <span>Directeur de publication : Guillaume THOMAS</span><br>
                            <span>Le capital social : 70 759 €</span><br>
                            <span>Le numéro RCS : 818 971 111 (RCS Bobigny)</span>
                        </p>
                        <p>L’hébergeur du service est Google Cloud Platform situé en Europe.</p>
                    </li>
                    <li>
                        <h2>Acceptation des CGU</h2>
                        <p>L’utilisation de la Plateforme implique l’acceptation intégrale et sans réserve des présentes CGU. En y accédant, l’utilisateur reconnaît en avoir pris connaissance et les accepter. Les CGU peuvent être modifiées par Open Mind Innovation et s’imposent à l’utilisateur dès leur mise en ligne. L’Utilisateur est donc invité à consulter régulièrement la dernière version mise à jour. Les conditions générales figurant en ligne sur la plateforme prévalent sur toute version imprimée de date antérieure.</p>
                    </li>
                    <li>
                        <h2>Accès à la Plateforme</h2>
                        <p>L'accès au service est réservé aux seules personnes majeures de plus de dix-huit (18) ans, et est réservé aux utilisateurs dans le cadre d’un accompagnement, d’une formation ou d’un atelier sollicité par leur employeur. L’utilisation de la Plateforme est strictement limitée à cette finalité et doit respecter les modalités définies par l’Éditeur.</p>
                    </li>

                    <li>
                        <h2>Utilisation de la Plateforme</h2>
                        <ol>
                            <li>
                                <h3>Finalité</h3>
                                <p>La Plateforme permet à l’Utilisateur d'échanger avec un agent conversationnel dans le cadre d'un accompagnement, d'une formation ou d'un atelier autour des outils d'IA générative et agentique.</p>
                            </li>
                            <li>
                                <h3>Restrictions</h3>
                                <p>Il est interdit d’utiliser la Plateforme pour :
                                <ul class="compact">
                                    <li>Des activités illégales, frauduleuses ou contraires à l’ordre public ;</li>
                                    <li>Diffuser des contenus offensants, diffamatoires ou discriminatoires ;</li>
                                    <li>Perturber son fonctionnement ;</li>
                                    <li>Collecter ou exploiter des données de manière non autorisée.</li>
                                </ul>
                                </p>
                            </li>
                        </ol>
                    </li>
                    <li>
                        <h2>Propriété Intellectuelle</h2>
                        <p>Les marques et services Open Mind Innovation sont protégés au titre des articles L.712-1 et suivants du code de la propriété intellectuelle. Toute représentation, reproduction ou diffusion, intégrale ou partielle de la marque Omind, sur quelque support que ce soit, sans l'autorisation expresse et préalable d’Open Mind Innovation constitue un acte de contrefaçon, sanctionné en application des articles L.716-1 du même code.</p>
                    </li>
                    <li>
                        <h2>Protection des Données</h2>
                        <ol>
                            <li>
                                <h3>Collecte des données</h3>
                                <p>La Plateforme ne collecte aucune donnée personnelle des utilisateurs, à l’exceptiondes adresses IP. La collecte des adresses IP repose sur l’intérêt légitime de l’Éditeur à assurer la sécurité de la Plateforme (Art. 6(1)(f) du RGPD). Les adresses IP sont conservées pour une durée maximale de 6 mois, puis supprimées automatiquement.<br>
                                    Ces adresses IP ne sont pas associées à d’autres informations permettant d’identifier directement un utilisateur.</p>
                            </li>
                            <li>
                                <h3>Données techniques anonymes</h3>
                                <p>Seules des données techniques anonymes (statistiques d’utilisation agrégées, logs de sessions non liés à une identité) peuvent être collectées pour améliorer la Plateforme. Ces données ne permettent pas d’identifier un utilisateur.</p>
                            </li>
                            <li>
                                <h3>Cookies et traceurs</h3>
                                <p>La Plateforme n’utilise aucun cookie ou traceur nécessitant un consentement.</p>
                            </li>
                            <li>
                                <h3>Droits des Utilisateurs</h3>
                                <p>Conformément à l'article 17 du RGPD, tout Utilisateur peut exercer ses droits concernant ses données (accès, rectification, effacement, portabilité, opposition, limitation). La demande de suppression doit être faite au Délégué à la Protection des Données (DPO) par mail via dpo@omind.me. Le délai de réponse à une demande conforme est d'un (1) mois maximum. Après identification des données concernées, la suppression est effectuée en base de données active. La purge des sauvegardes est effectuée selon la politique de rétention de l’Éditeur sous maximum 14 jours. Une fois la suppression effectuée, un rapport d'effacement (logs, captures d’écran) est fourni ensuite à l'instigateur de la demande sous un (1) mois.</p>
                            </li>
                        </ol>
                    </li>

                    <li>
                        <h2>Responsabilités</h2>
                        <ol>
                            <li>
                                <h3>Responsabilité de l’Éditeur</h3>
                                <p>L’Éditeur s’engage à maintenir la Plateforme accessible et fonctionnelle, sous réserve des limitations techniques et des cas de force majeure. Il ne peut être tenu responsable des dommages indirects (perte de données, préjudices commerciaux, etc.) résultant de son utilisation.<br> Les Données d’Entrée fournies par l’Utilisateur ne sont pas utilisées pour entraîner ou améliorer les modèles d’IA, sauf accord explicite de l’Utilisateur. Elles sont traitées uniquement pour générer les Données de Sortie et sont supprimées après la session, sauf si l’Utilisateur les sauvegarde explicitement.</p>
                            </li>
                            <li>
                                <h3>Responsabilité de l’Utilisateur</h3>
                                <p>L’utilisateur est seul responsable :
                                <ul class="compact">
                                    <li>Des contenus qu’il transmet via la Plateforme et de leur conformité avec les lois en vigueur ;</li>
                                    <li>De toutes les Données d’Entrée qu’il utilise. Il déclare et garantit disposer de tous les droits, licences et autorisations nécessaires pour les fournir à la Plateforme. Il s’engage à ne pas saisir de données sensibles (au sens de l’Art. 9 du RGPD) via la Plateforme ; </li>
                                    <li>De l’utilisation des Données de Sortie et de l’évaluation de leur exactitude etde leur adéquation à son cas d’usage. Il s’engage à ne pas présenter les Données de Sortie comme ayant été générées par un être humain alors qu’elles ont été générées par la Plateforme.</li>
                                </ul>
                                </p>
                            </li>
                        </ol>
                    </li>

                    <li>
                        <h2>Exactitude des Données de Sortie</h2>
                        <p>La Plateforme repose sur des grands modèles de langage (LLMs), une technologie en constante évolution. Pour cette raison, les Données de Sortie générées peuvent occasionnellement être inexactes.</p>
                        <p>La Plateforme n’est pas une source d’information faisant autorité ou infaillible. Les modèles d’IA utilisés peuvent présenter des biais ou des erreurs. Vous ne devez pas vous fier aux résultats générés comme seule source de vérité ou comme substitut à des conseils professionnels (juridiques, fiscaux, médicaux, etc.).</p>
                        <p>Utilisez la Plateforme avec prudence : vérifiez toujours la fiabilité, l’exactitude et l’exhaustivité des Données de Sortie avant de vous y fier à des fins personnelles ou professionnelles, ou de les mettre à la disposition de tiers (y compris en les publiant).</p>
                    </li>
                    <li>
                        <h2>Limitation de Garantie</h2>
                        <p>La Plateforme est fournie « en l’état ». L’Éditeur ne garantit pas son absence de bugs ni sonadaptation à tous les besoins. Les réponses de l’agent conversationnel sont indicatives et ne constituent pas des conseils professionnels.</p>
                    </li>
                    <li>
                        <h2>Résiliation</h2>
                        <p>L’Éditeur peut suspendre ou résilier l’accès à la Plateforme pour tout utilisateur ne respectant pas les CGU, sans préavis ni indemnité.</p>
                    </li>
                    <li>
                        <h2>Loi Applicable et Litiges</h2>
                        <p>Les présentes CGU sont régies par le droit français. Tout litige sera soumis à la compétence exclusive des tribunaux de Bobigny.</p>
                    </li>
                </ol>
            </template>
            <template v-else>
                <h1>Terms and Conditions of Use (T&C)</h1>
                <p>Last updated: July 7, 2026</p>
                <br>
                <ol>
                    <li>
                        <h2>Preamble</h2>
                        <p>These Terms and Conditions of Use (T&C) govern the conditions of access, the terms of use of the services, and the respective rights and obligations of Users when using the services available via the <a href="https://ai.omind.me">ai.omind.me Platform</a> (hereinafter referred to as "the Platform"), developed by Open Mind Innovation.</p>
                    </li>

                    <li>
                        <h2>Publication director and host</h2>
                        <p>
                            <span>Open Mind Innovation SAS</span><br>
                            <span>Head office: 7 Place du 11 Novembre 1918 - 93000 Bobigny (FRANCE)</span><br>
                            <span>Phone: +33 (0)6 59 35 99 63</span><br>
                            <span>Publication Director: Guillaume THOMAS</span><br>
                            <span>Capital: €70,759</span><br>
                            <span>Companies registration number: 818 971 111 (RCS Bobigny)</span>
                        </p>
                        <p>The service is hosted by Google Cloud Platform, located in Europe.</p>
                    </li>

                    <li>
                        <h2>Acceptance of the T&C</h2>
                        <p>Use of the Platform implies full and unconditional acceptance of these T&C. By accessing it, the User acknowledges having read and accepted them. The T&C may be modified by Open Mind Innovation and are binding on the User as soon as they are published online. Users are therefore invited to regularly consult the latest updated version. The general terms and conditions displayed online on the Platform take precedence over any printed version of an earlier date.</p>
                    </li>

                    <li>
                        <h2>Access to the Platform</h2>
                        <p>Access to the service is reserved solely for individuals over the age of eighteen (18) and is intended for users as part of support, training, or a workshop requested by their employer. Use of the Platform is strictly limited to this purpose and must comply with the terms defined by the Publisher.</p>
                    </li>

                    <li>
                        <h2>Use of the Platform</h2>
                        <ol>
                            <li>
                                <h3>Purpose</h3>
                                <p>The Platform allows the User to interact with a conversational agent as part of support, training, or a workshop focused on generative and agentic AI tools.</p>
                            </li>
                            <li>
                                <h3>Restrictions</h3>
                                <p>It is prohibited to use the Platform for:
                                <ul class="compact">
                                    <li>Illegal, fraudulent, or activities contrary to public order;</li>
                                    <li>Disseminating offensive, defamatory, or discriminatory content;</li>
                                    <li>Disrupting its operation;</li>
                                    <li>Collecting or exploiting data in an unauthorized manner.</li>
                                </ul>
                                </p>
                            </li>
                        </ol>
                    </li>

                    <li>
                        <h2>Intellectual Property</h2>
                        <p>The Open Mind Innovation brands and services are protected under Articles L.712-1 and following of the French Intellectual Property Code. Any representation, reproduction, or distribution, whether full or partial, of the Omind brand on any medium whatsoever, without the prior and express authorization of Open Mind Innovation, constitutes an act of counterfeiting, punishable under Article L.716-1 of the same code.</p>
                    </li>

                    <li>
                        <h2>Data Protection</h2>
                        <ol>
                            <li>
                                <h3>Data Collection</h3>
                                <p>The Platform does not collect any personal data from users, except for IP addresses. The collection of IP addresses is based on the legitimate interest of the Publisher to ensure the security of the Platform (Art. 6(1)(f) of the GDPR). IP addresses are stored for a maximum period of 6 months and then automatically deleted.<br>
                                    These IP addresses are not associated with other information that could directly identify a user.</p>
                            </li>
                            <li>
                                <h3>Anonymous Technical Data</h3>
                                <p>Only anonymous technical data (aggregated usage statistics, session logs not linked to an identity) may be collected to improve the Platform. This data does not allow the identification of a user.</p>
                            </li>
                            <li>
                                <h3>Cookies and Trackers</h3>
                                <p>The Platform does not use any cookies or trackers requiring consent.</p>
                            </li>
                            <li>
                                <h3>User Rights</h3>
                                <p>In accordance with Article 17 of the GDPR, any User may exercise their rights regarding their data (access, rectification, erasure, portability, objection, restriction). Requests for deletion must be made to the Data Protection Officer (DPO) by email at dpo@omind.me. The response time for a compliant request is a maximum of one (1) month. After identifying the relevant data, deletion is carried out in the active database. The purging of backups is performed according to the Publisher's retention policy within a maximum of 14 days. Once deletion is completed, a deletion report (logs, screenshots) is then provided to the requester within one (1) month.</p>
                            </li>
                        </ol>
                    </li>

                    <li>
                        <h2>Liabilities</h2>
                        <ol>
                            <li>
                                <h3>Publisher's Liability</h3>
                                <p>The Publisher commits to keeping the Platform accessible and functional, subject to technical limitations and force majeure events. It cannot be held liable for indirect damages (data loss, commercial damages, etc.) resulting from its use.<br>
                                    The Input Data provided by the User is not used to train or improve AI models, unless explicitly agreed by the User. It is processed solely to generate Output Data and is deleted after the session, unless the User explicitly saves it.</p>
                            </li>
                            <li>
                                <h3>User's Liability</h3>
                                <p>The User is solely responsible for:
                                <ul class="compact">
                                    <li>The content they transmit via the Platform and its compliance with applicable laws;</li>
                                    <li>All Input Data they use. They declare and guarantee that they have all the necessary rights, licenses, and permissions to provide it to the Platform. They undertake not to enter sensitive data (as defined in Art. 9 of the GDPR) via the Platform;</li>
                                    <li>The use of Output Data and the assessment of its accuracy and suitability for their use case. They undertake not to present Output Data as having been generated by a human when it was generated by the Platform.</li>
                                </ul>
                                </p>
                            </li>
                        </ol>
                    </li>

                    <li>
                        <h2>Accuracy of Output Data</h2>
                        <p>The Platform relies on large language models (LLMs), a constantly evolving technology. For this reason, the generated Output Data may occasionally be inaccurate.</p>
                        <p>The Platform is not an authoritative or infallible source of information. The AI models used may exhibit biases or errors. You should not rely on the generated results as the sole source of truth or as a substitute for professional advice (legal, tax, medical, etc.).</p>
                        <p>Use the Platform with caution: always verify the reliability, accuracy, and completeness of the Output Data before relying on it for personal or professional purposes or making it available to third parties (including by publishing it).</p>
                    </li>

                    <li>
                        <h2>Limitation of Warranty</h2>
                        <p>The Platform is provided "as is." The Publisher does not guarantee the absence of bugs or its suitability for all needs. The responses from the conversational agent are indicative and do not constitute professional advice.</p>
                    </li>

                    <li>
                        <h2>Termination</h2>
                        <p>The Publisher may suspend or terminate access to the Platform for any user who does not comply with the T&C, without notice or compensation.</p>
                    </li>

                    <li>
                        <h2>Applicable Law and Disputes</h2>
                        <p>These T&C are governed by French law. Any dispute shall be subject to the exclusive jurisdiction of the courts of Bobigny.</p>
                    </li>
                </ol>
            </template>
        </div>
    </div>

</template>

<style lang="scss">
.legal-terms {
    font-size: 14px;
    line-height: 16px;
    font-weight: 300;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    //padding: var(--spacing-xl);
    white-space: pre-wrap;
    align-items: center;

    .legal-terms-inner {
        //max-width: 800px;
        display: flex;
        flex-direction: column;
    }

    h1 {
        display: block;
        margin: 0 0 1.5rem;
        font-size: 22px;
        line-height: 26px;
        font-weight: 600;
    }

    h2 {
        display: block;
        margin: 0 0 0.75rem;
        font-size: 14px;
        line-height: 21px;
        font-weight: 700;
    }

    h3 {
        display: block;
        margin: 1rem 0 0.75rem;
        font-size: 14px;
        line-height: 19px;
        font-weight: 500;
    }

    p {
        margin: 0 0 0.5rem;
    }

    ul,
    ol {
        padding: 0;
        padding-inline-start: 1.5rem;
        margin: 0;

        li {
            margin: 0 0 2rem;
        }

        &.compact {
            li {
                margin: 0 0 0.5rem;
            }
        }
    }
}
</style>